import { useCallback, useEffect, useId, useRef, type ReactNode } from 'react';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/primitives';
import { useEscape } from '@/hooks/useEscape';
import { useMountTransition } from '@/hooks/useMountTransition';
import { useOutsideClick } from '@/hooks/useOutsideClick';
import { useScrollLock } from '@/hooks/useScrollLock';

/**
 * Overlays: dialog, drawer, popover.
 *
 * All three share one set of rules, because an operator should not have to
 * learn three: Escape closes, focus moves inside on open and comes back on
 * close, the page behind does not scroll, and the exit animation actually runs
 * instead of the node vanishing mid-fade.
 *
 * The backdrop does not close a dialog or drawer. A half-filled form for an
 * operation on live infrastructure should not disappear on a stray click. A
 * popover does close that way — nothing is being typed into it that a second
 * click cannot redo.
 */

const DURATION = 180;

/** Moves focus to the first sensible control and returns it afterwards. */
function useFocusTrap(active: boolean, ref: { current: HTMLElement | null }): void {
  useEffect(() => {
    if (!active) return;

    const previous = document.activeElement;
    const container = ref.current;

    const focusables = (): HTMLElement[] =>
      [
        ...(container?.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ) ?? []),
      ].filter((element) => element.offsetParent !== null);

    // A frame's delay: the element is in the DOM but not yet painted, and
    // focusing an unpainted node scrolls the page in some browsers.
    const timer = setTimeout(() => {
      const [first] = focusables();
      (first ?? container)?.focus();
    }, 0);

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== 'Tab') return;

      const elements = focusables();
      if (elements.length === 0) return;

      const first = elements[0]!;
      const last = elements[elements.length - 1]!;

      // Tab must not walk out of an overlay into the page behind it, which is
      // inert to the eye and perfectly reachable to the keyboard.
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('keydown', onKeyDown);
      if (previous instanceof HTMLElement) previous.focus();
    };
  }, [active, ref]);
}

export interface OverlayProps {
  readonly open: boolean;
  readonly title: string;
  /** One line under the title. Context, not instructions. */
  readonly description?: string;
  readonly onClose: () => void;
  readonly footer?: ReactNode;
  readonly children: ReactNode;
}

export interface DialogProps extends OverlayProps {
  readonly size?: 'sm' | 'md' | 'lg';
}

export function Dialog({
  open,
  title,
  description,
  onClose,
  footer,
  size = 'md',
  children,
}: DialogProps): ReactNode {
  const { mounted, phase } = useMountTransition(open, DURATION);
  const panel = useRef<HTMLDivElement | null>(null);
  const titleId = useId();

  useScrollLock(mounted);
  useEscape(mounted, onClose);
  useFocusTrap(mounted, panel);

  if (!mounted) return null;

  return (
    <div className={`overlay overlay-${phase}`} role="presentation">
      <div className="overlay-backdrop" />
      <div
        ref={panel}
        className={`dialog dialog-${size}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
      >
        <header className="overlay-head">
          <div>
            <h2 id={titleId} className="overlay-title">
              {title}
            </h2>
            {description ? <p className="overlay-description">{description}</p> : null}
          </div>
          <Button variant="ghost" size="sm" className="btn-icon" aria-label="Закрыть" onClick={onClose}>
            <X size={16} aria-hidden="true" />
          </Button>
        </header>

        <div className="overlay-body">{children}</div>
        {footer ? <footer className="overlay-foot">{footer}</footer> : null}
      </div>
    </div>
  );
}

export interface DrawerProps extends OverlayProps {
  readonly width?: 'sm' | 'md' | 'lg';
}

/** Slides in from the right, leaving the page it came from visible behind it —
 *  which is the whole reason to use one instead of a dialog. */
export function Drawer({
  open,
  title,
  description,
  onClose,
  footer,
  width = 'md',
  children,
}: DrawerProps): ReactNode {
  const { mounted, phase } = useMountTransition(open, DURATION);
  const panel = useRef<HTMLDivElement | null>(null);
  const titleId = useId();

  useScrollLock(mounted);
  useEscape(mounted, onClose);
  useFocusTrap(mounted, panel);

  if (!mounted) return null;

  return (
    <div className={`overlay overlay-drawer overlay-${phase}`} role="presentation">
      <div className="overlay-backdrop" onClick={onClose} role="presentation" />
      <div
        ref={panel}
        className={`drawer drawer-${width}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
      >
        <header className="overlay-head">
          <div>
            <h2 id={titleId} className="overlay-title">
              {title}
            </h2>
            {description ? <p className="overlay-description">{description}</p> : null}
          </div>
          <Button variant="ghost" size="sm" className="btn-icon" aria-label="Закрыть" onClick={onClose}>
            <X size={16} aria-hidden="true" />
          </Button>
        </header>

        <div className="overlay-body">{children}</div>
        {footer ? <footer className="overlay-foot">{footer}</footer> : null}
      </div>
    </div>
  );
}

export interface ConfirmDialogProps {
  readonly open: boolean;
  readonly title: string;
  /** Explicitly allows `undefined`: the description usually comes from the row
   *  being acted on, which is null while the dialog is closing. */
  readonly description?: string | undefined;
  readonly confirmLabel: string;
  readonly cancelLabel?: string;
  readonly destructive?: boolean;
  readonly busy?: boolean;
  readonly error?: string | undefined;
  readonly onConfirm: () => void;
  readonly onClose: () => void;
  readonly children?: ReactNode;
}

/**
 * The standard confirmation. Exists so that every "are you sure" in the panel
 * looks and behaves the same, rather than each screen inventing its own.
 */
export function ConfirmDialog({
  open,
  title,
  description,
  confirmLabel,
  cancelLabel = 'Отмена',
  destructive = false,
  busy = false,
  error,
  onConfirm,
  onClose,
  children,
}: ConfirmDialogProps): ReactNode {
  // Closing while the action is in flight would leave the operator with no way
  // to see whether it succeeded.
  const close = useCallback(() => {
    if (!busy) onClose();
  }, [busy, onClose]);

  return (
    <Dialog
      open={open}
      title={title}
      {...(description ? { description } : {})}
      onClose={close}
      size="sm"
      footer={
        <>
          <Button onClick={close} disabled={busy}>
            {cancelLabel}
          </Button>
          <Button variant={destructive ? 'danger-solid' : 'primary'} busy={busy} onClick={onConfirm}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      {children}
      {error ? (
        <p className="field-error" role="alert">
          {error}
        </p>
      ) : null}
    </Dialog>
  );
}

export interface PopoverProps {
  readonly open: boolean;
  readonly onClose: () => void;
  /** Where it hangs relative to its anchor. */
  readonly align?: 'start' | 'end';
  readonly label: string;
  readonly children: ReactNode;
}

/**
 * A small panel anchored to whatever wraps it.
 *
 * The parent must be `position: relative` — that is the whole positioning
 * model, deliberately, because a floating-element library is a large thing to
 * carry for menus that never leave the viewport.
 */
export function Popover({ open, onClose, align = 'start', label, children }: PopoverProps): ReactNode {
  const { mounted, phase } = useMountTransition(open, 140);
  const panel = useRef<HTMLDivElement | null>(null);

  useEscape(mounted, onClose);
  useOutsideClick(mounted, panel, onClose);
  useFocusTrap(mounted, panel);

  if (!mounted) return null;

  return (
    <div
      ref={panel}
      className={`popover popover-${align} popover-${phase}`}
      role="dialog"
      aria-label={label}
      tabIndex={-1}
    >
      {children}
    </div>
  );
}
